import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Tabs,
  Row,
  Col,
  Statistic,
  Table,
  DatePicker,
  Space,
  Button,
  Typography,
  message,
  Select
} from 'antd';
import {
  DownloadOutlined,
  ReloadOutlined,
  BarChartOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { getSales, getStats } from '../services/api';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const COLORS = ['#1677ff', '#52c41a', '#fa8c16', '#eb2f96', '#722ed1', '#13c2c2', '#f5222d'];

const money = (v) => Number(v || 0);

/**
 * 数据报表（市面常见 ERP 报表结构）
 * - 可筛选时间范围
 * - 汇总指标 + 明细表 + 维度分析
 * - 可导出
 * 注意：不做「数据概览」式仪表盘复刻
 */
const Reports = ({ isMobile }) => {
  const [loading, setLoading] = useState(false);
  const [sales, setSales] = useState([]);
  const [stats, setStats] = useState(null);
  const [range, setRange] = useState([dayjs().startOf('month'), dayjs().endOf('day')]);
  const [categoryFilter, setCategoryFilter] = useState(undefined);

  const loadData = async () => {
    setLoading(true);
    try {
      const [salesRes, statsRes] = await Promise.all([
        getSales({ page: 1, limit: 2000 }),
        getStats()
      ]);
      const list = Array.isArray(salesRes?.sales)
        ? salesRes.sales
        : (Array.isArray(salesRes) ? salesRes : []);
      setSales(list.filter((s) => (s.status || '') !== 'cancelled'));
      setStats(statsRes || null);
    } catch (e) {
      message.error(e?.message || '加载报表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const filteredSales = useMemo(() => {
    const [start, end] = range || [];
    return (sales || []).filter((s) => {
      const t = dayjs(s.created_at || s.saleDate);
      if (!t.isValid()) return false;
      if (start && t.isBefore(start, 'day')) return false;
      if (end && t.isAfter(end, 'day')) return false;
      if (categoryFilter) {
        const cat = s.account?.category || s.category || '';
        if (cat !== categoryFilter) return false;
      }
      return true;
    });
  }, [sales, range, categoryFilter]);

  const summary = useMemo(() => {
    const salesAmt = filteredSales.reduce((sum, s) => sum + money(s.salePrice ?? s.price), 0);
    const profitAmt = filteredSales.reduce((sum, s) => sum + money(s.profit), 0);
    return {
      sales: salesAmt,
      profit: profitAmt,
      count: filteredSales.length,
      avg: filteredSales.length ? salesAmt / filteredSales.length : 0
    };
  }, [filteredSales]);

  const byDay = useMemo(() => {
    const map = {};
    filteredSales.forEach((s) => {
      const key = dayjs(s.created_at || s.saleDate).format('YYYY-MM-DD');
      if (!map[key]) map[key] = { day: key, sales: 0, profit: 0, count: 0 };
      map[key].sales += money(s.salePrice ?? s.price);
      map[key].profit += money(s.profit);
      map[key].count += 1;
    });
    return Object.values(map).sort((a, b) => a.day.localeCompare(b.day));
  }, [filteredSales]);

  const byCategory = useMemo(() => {
    const map = {};
    filteredSales.forEach((s) => {
      const name = s.account?.category || s.category || '未分类';
      if (!map[name]) map[name] = { name, sales: 0, profit: 0, count: 0 };
      map[name].sales += money(s.salePrice ?? s.price);
      map[name].profit += money(s.profit);
      map[name].count += 1;
    });
    return Object.values(map).sort((a, b) => b.sales - a.sales);
  }, [filteredSales]);

  const inventoryRows = useMemo(() => {
    const cats = stats?.categoryStats || [];
    return cats.map((c) => ({
      key: c.name,
      name: c.name || '未分类',
      total: c.count || 0,
      pending: c.pending || 0,
      sold: c.sold || 0,
      banned: c.banned || 0
    }));
  }, [stats]);

  const categoryOptions = useMemo(() => {
    const set = new Set();
    (sales || []).forEach((s) => {
      const cat = s.account?.category || s.category;
      if (cat) set.add(cat);
    });
    (stats?.categoryStats || []).forEach((c) => {
      if (c.name) set.add(c.name);
    });
    return Array.from(set).map((v) => ({ label: v, value: v }));
  }, [sales, stats]);

  const exportCsv = (rows, filename, headers) => {
    const escape = (val) => {
      const s = val == null ? '' : String(val);
      if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const lines = [headers.map((h) => h.title).join(',')];
    rows.forEach((row) => {
      lines.push(headers.map((h) => escape(typeof h.render === 'function' ? h.render(row) : row[h.key])).join(','));
    });
    const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const saleColumns = [
    {
      title: '销售时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 170,
      render: (_, r) => {
        const t = dayjs(r.created_at || r.saleDate);
        return t.isValid() ? t.format('YYYY-MM-DD HH:mm') : '-';
      }
    },
    {
      title: '账号',
      key: 'username',
      render: (_, r) => r.account?.username || r.username || '-'
    },
    {
      title: '分类',
      key: 'category',
      width: 120,
      render: (_, r) => r.account?.category || r.category || '未分类'
    },
    {
      title: '买家',
      dataIndex: 'buyerName',
      key: 'buyerName',
      width: 120,
      render: (v) => v || '-'
    },
    {
      title: '销售额',
      key: 'price',
      width: 110,
      align: 'right',
      render: (_, r) => `¥${money(r.salePrice ?? r.price).toFixed(2)}`
    },
    {
      title: '利润',
      dataIndex: 'profit',
      key: 'profit',
      width: 110,
      align: 'right',
      render: (v) => `¥${money(v).toFixed(2)}`
    }
  ];

  const setQuickRange = (key) => {
    const now = dayjs();
    if (key === 'today') setRange([now.startOf('day'), now.endOf('day')]);
    if (key === '7d') setRange([now.subtract(6, 'day').startOf('day'), now.endOf('day')]);
    if (key === 'month') setRange([now.startOf('month'), now.endOf('day')]);
    if (key === 'year') setRange([now.startOf('year'), now.endOf('day')]);
  };

  const tabItems = [
    {
      key: 'sales',
      label: '销售明细',
      children: (
        <div>
          <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
            <Col xs={12} sm={6}>
              <Card size="small"><Statistic title="销售额" value={summary.sales} prefix="¥" precision={2} valueStyle={{ color: '#1677ff' }} /></Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small"><Statistic title="利润" value={summary.profit} prefix="¥" precision={2} valueStyle={{ color: '#52c41a' }} /></Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small"><Statistic title="成交笔数" value={summary.count} /></Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small"><Statistic title="客单价" value={summary.avg} prefix="¥" precision={2} /></Card>
            </Col>
          </Row>

          {!isMobile && byDay.length > 0 && (
            <Card title="区间销售趋势" size="small" style={{ marginBottom: 16 }}>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={byDay}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v, n) => (n === '笔数' ? v : [`¥${Number(v).toFixed(2)}`, n])} />
                  <Legend />
                  <Bar dataKey="sales" name="销售额" fill="#1677ff" />
                  <Bar dataKey="profit" name="利润" fill="#52c41a" />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          )}

          <Card
            size="small"
            title={`明细（${filteredSales.length}）`}
            extra={
              <Button
                icon={<DownloadOutlined />}
                onClick={() => exportCsv(
                  filteredSales,
                  `销售明细_${dayjs().format('YYYYMMDD')}.csv`,
                  [
                    { title: '销售时间', key: 't', render: (r) => dayjs(r.created_at || r.saleDate).format('YYYY-MM-DD HH:mm') },
                    { title: '账号', key: 'u', render: (r) => r.account?.username || r.username || '' },
                    { title: '分类', key: 'c', render: (r) => r.account?.category || r.category || '' },
                    { title: '买家', key: 'b', render: (r) => r.buyerName || '' },
                    { title: '销售额', key: 'p', render: (r) => money(r.salePrice ?? r.price).toFixed(2) },
                    { title: '利润', key: 'f', render: (r) => money(r.profit).toFixed(2) }
                  ]
                )}
              >
                导出
              </Button>
            }
          >
            <Table
              rowKey={(r) => r.id || `${r.account_id}-${r.created_at}`}
              loading={loading}
              columns={saleColumns}
              dataSource={filteredSales}
              size="small"
              scroll={{ x: 720 }}
              pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (t) => `共 ${t} 条` }}
            />
          </Card>
        </div>
      )
    },
    {
      key: 'category',
      label: '分类汇总',
      children: (
        <div>
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={10}>
              <Card title="销售额占比" size="small">
                {byCategory.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={byCategory}
                        dataKey="sales"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={90}
                        label={!isMobile ? ({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%` : false}
                      >
                        {byCategory.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => `¥${Number(v).toFixed(2)}`} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ textAlign: 'center', padding: 48, color: '#999' }}>所选区间暂无数据</div>
                )}
              </Card>
            </Col>
            <Col xs={24} lg={14}>
              <Card
                title="分类明细"
                size="small"
                extra={
                  <Button
                    icon={<DownloadOutlined />}
                    onClick={() => exportCsv(
                      byCategory,
                      `分类汇总_${dayjs().format('YYYYMMDD')}.csv`,
                      [
                        { title: '分类', key: 'name' },
                        { title: '笔数', key: 'count' },
                        { title: '销售额', key: 'sales', render: (r) => money(r.sales).toFixed(2) },
                        { title: '利润', key: 'profit', render: (r) => money(r.profit).toFixed(2) }
                      ]
                    )}
                  >
                    导出
                  </Button>
                }
              >
                <Table
                  rowKey="name"
                  size="small"
                  pagination={false}
                  dataSource={byCategory}
                  columns={[
                    { title: '分类', dataIndex: 'name' },
                    { title: '笔数', dataIndex: 'count', width: 80 },
                    { title: '销售额', dataIndex: 'sales', align: 'right', render: (v) => `¥${money(v).toFixed(2)}` },
                    { title: '利润', dataIndex: 'profit', align: 'right', render: (v) => `¥${money(v).toFixed(2)}` }
                  ]}
                />
              </Card>
            </Col>
          </Row>
        </div>
      )
    },
    {
      key: 'inventory',
      label: '库存结构',
      children: (
        <Card
          title="按分类库存（当前快照，不受上方时间筛选影响）"
          size="small"
          extra={
            <Button
              icon={<DownloadOutlined />}
              onClick={() => exportCsv(
                inventoryRows,
                `库存结构_${dayjs().format('YYYYMMDD')}.csv`,
                [
                  { title: '分类', key: 'name' },
                  { title: '总数', key: 'total' },
                  { title: '待售', key: 'pending' },
                  { title: '已售', key: 'sold' },
                  { title: '起号', key: 'banned' }
                ]
              )}
            >
              导出
            </Button>
          }
        >
          <Table
            rowKey="key"
            loading={loading}
            size="small"
            dataSource={inventoryRows}
            pagination={false}
            columns={[
              { title: '分类', dataIndex: 'name' },
              { title: '总数', dataIndex: 'total', width: 90 },
              { title: '待售', dataIndex: 'pending', width: 90 },
              { title: '已售', dataIndex: 'sold', width: 90 },
              { title: '起号', dataIndex: 'banned', width: 90 }
            ]}
          />
        </Card>
      )
    }
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>
            <BarChartOutlined style={{ marginRight: 8 }} />
            数据报表
          </Title>
          <Text type="secondary">按时间与维度下钻分析，支持导出（非仪表盘概览）</Text>
        </div>
        <Space wrap>
          <Button onClick={() => setQuickRange('today')}>今日</Button>
          <Button onClick={() => setQuickRange('7d')}>近7天</Button>
          <Button onClick={() => setQuickRange('month')}>本月</Button>
          <Button onClick={() => setQuickRange('year')}>本年</Button>
          <RangePicker
            value={range}
            onChange={(v) => setRange(v)}
            allowClear={false}
            style={{ maxWidth: isMobile ? '100%' : undefined }}
          />
          <Select
            allowClear
            placeholder="分类筛选"
            style={{ width: 140 }}
            options={categoryOptions}
            value={categoryFilter}
            onChange={setCategoryFilter}
          />
          <Button icon={<ReloadOutlined />} onClick={loadData} loading={loading}>
            刷新
          </Button>
        </Space>
      </div>

      <Tabs items={tabItems} />
    </div>
  );
};

export default Reports;
